import type { ReactNode } from "react"
import { createContext, useContext } from "react"

import { cn } from "@productlane/lib/src/cn"

// True only inside the settings layout. Lets cards pull out by 14px to align
// with the page header, without affecting the same cards rendered in popovers.
export const SettingsCardInsetContext = createContext(false)

// sm+ only: bordered boxes bleed 14px so their inner content lines up with the
// page header; on mobile they stay within the page padding.
export const SETTINGS_INSET_CLASS = "sm:-mx-3.5"

// The page's sm+ side padding. It must clear the inset above, or a bled card
// sits flush against the sidebar once the column fills the viewport.
export const SETTINGS_PAGE_PADDING_X_CLASS = "sm:px-8"

// The one card surface for every settings card: 10px radius and a hairline
// shadow instead of a border, so cards sit on the page rather than boxing it in.
export const SETTINGS_CARD_CLASS =
	"rounded-[10px] bg-white shadow-card dark:bg-gray-850 dark:shadow-card-dark"

// A notice strip across the card's top edge: it cancels the card padding, owns
// the hairline under it, and drops the divide border the next row would draw.
export const SETTINGS_CARD_NOTICE_CLASS =
	"settings-card-notice -mx-4 -mt-4 rounded-t-[10px] border-b border-gray-300/80 px-4 py-2.5 text-xs max-sm:mt-0 max-sm:rounded-t-2xl dark:border-gray-700/80 [&+*]:!border-t-0"

interface Props {
	children: ReactNode
	className?: string
	notice?: ReactNode
}

export const SettingsContainer = ({ children, className, notice }: Props) => {
	const inset = useContext(SettingsCardInsetContext)
	return (
		<div
			className={cn(
				SETTINGS_CARD_CLASS,
				"divide-y divide-gray-300/80 p-4 dark:divide-gray-700/80",
				// Setting rows sit 20px from each divider and rows that open a page keep 16px; outer edges
				// and divider-free cards keep the page's spacing.
				"sm:[&:not(.divide-y-0)>*:not(:first-child)]:!mt-0 sm:[&:not(.divide-y-0)>*:not(:first-child):not(.settings-opens-row)]:!pt-5 sm:[&:not(.divide-y-0)>*:not(:last-child):not(.settings-card-notice)]:!mb-0 sm:[&:not(.divide-y-0)>*:not(:last-child):not(.settings-card-notice):not(.settings-opens-row)]:!pb-5",
				// iOS-style flush rows on mobile: no inter-row gap, hairline dividers,
				// 16px radius, and uniform 14px row padding overriding per-page pt-4.
				"max-sm:space-y-0 max-sm:rounded-2xl max-sm:py-0 max-sm:[&>*]:!py-3.5",
				inset && SETTINGS_INSET_CLASS,
				className,
			)}
		>
			{notice}
			{children}
		</div>
	)
}
